import React, { useState, useEffect, createContext, useContext } from 'react';
import { toast } from 'sonner';

// Portal Auth Context
const PortalAuthContext = createContext(null);

export const usePortalAuth = () => useContext(PortalAuthContext);

// WP-P2F — the two routes a half-signed-in customer may use while they are
// setting up two-factor authentication. Nothing else accepts a set-up token.
const SETUP_ROUTES = ['/portal/setup-2fa', '/portal/enable-2fa'];

// API helper for portal
const portalApi = {
  baseUrl: process.env.REACT_APP_BACKEND_URL || '',
  token: null,
  setupToken: null,

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('portal_token', token);
    } else {
      localStorage.removeItem('portal_token');
    }
  },

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('portal_token');
    }
    return this.token;
  },

  // WP-P2F — the 15-minute set-up token lives in sessionStorage, not
  // localStorage: it is not a session, it dies with the tab, and it must never
  // be mistaken for one by code that reads portal_token.
  setSetupToken(token) {
    this.setupToken = token;
    try {
      if (token) {
        sessionStorage.setItem('portal_setup_token', token);
      } else {
        sessionStorage.removeItem('portal_setup_token');
      }
    } catch (e) {
      // Private browsing: the token still works for this page load.
    }
  },

  getSetupToken() {
    if (!this.setupToken) {
      try {
        this.setupToken = sessionStorage.getItem('portal_setup_token');
      } catch (e) {
        this.setupToken = null;
      }
    }
    return this.setupToken;
  },

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}/api/customer-portal${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    const token = this.getToken();
    // WP-P2F — on the two set-up routes, fall back to the set-up token when
    // there is no session yet. A real session always wins, so a signed-in
    // customer opening 2FA from Account Settings is unaffected.
    const isSetupRoute = SETUP_ROUTES.includes(endpoint);
    const setupToken = isSetupRoute ? this.getSetupToken() : null;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (setupToken) {
      headers['Authorization'] = `Bearer ${setupToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      if (isSetupRoute && !token) {
        // WP-P2F — do NOT treat this as "session expired" and bounce: there is
        // no session to expire, and redirecting here would throw the customer
        // round a login -> set-up -> login loop. Let the set-up screen show the
        // message (usually "your set-up link has expired, sign in again").
        let detail = 'Your set-up link has expired. Please sign in again.';
        try {
          const body = await response.json();
          if (body?.detail) detail = body.detail;
        } catch (e) { /* non-JSON error body */ }
        throw new Error(detail);
      }
      this.setToken(null);
      window.location.href = '/customer-portal/login';
      throw new Error('Session expired');
    }

    const data = await response.json();
    
    if (!response.ok) {
      // D-SEC4: the per-address rate limiter answers with {error: "Rate limit
      // exceeded: <our sentence>"} and no detail, so without this the customer
      // would only see "Request failed". The account lockout still carries a
      // detail, so its own message keeps coming through untouched.
      if (response.status === 429 && !data.detail && typeof data.error === 'string') {
        throw new Error(data.error.replace(/^Rate limit exceeded:\s*/, ''));
      }
      throw new Error(data.detail || 'Request failed');
    }

    return data;
  },

  // Auth
  login: (credentials) => portalApi.request('/auth/login', { method: 'POST', body: JSON.stringify(credentials) }),
  // NOTE: setup2FA is defined once, further down, against /portal/setup-2fa
  // (the pending-secret flow). A duplicate key here used to silently discard
  // the /auth/setup-2fa mapping — keep a single definition.
  verify2FA: (code) => portalApi.request('/auth/verify-2fa', { method: 'POST', body: JSON.stringify({ code }) }),
  changePassword: (data) => portalApi.request('/auth/change-password', { method: 'POST', body: JSON.stringify(data) }),
  
  // Products
  getProducts: () => portalApi.request('/products'),
  
  // Orders
  getOrders: () => portalApi.request('/orders'),
  createOrder: (data) => portalApi.request('/orders', { method: 'POST', body: JSON.stringify(data) }),
  
  // Quotes
  getQuotes: () => portalApi.request('/quotes'),
  createQuote: (data) => portalApi.request('/quotes', { method: 'POST', body: JSON.stringify(data) }),
  editQuote: (quoteId, data) => portalApi.request(`/quotes/${quoteId}`, { method: 'PUT', body: JSON.stringify(data) }),
  acceptQuote: (quoteId) => portalApi.request(`/quotes/${quoteId}/accept`, { method: 'POST' }),
  
  // Stock
  getStock: () => portalApi.request('/stock'),
  getHoldingPallets: (holdingId) => portalApi.request(`/stock/${holdingId}/pallets`),
  // (no downloadStockList helper: /stock/download returns a binary XLSX, so
  // components fetch it directly as a blob — request() would JSON-parse it)
  // Build a fully-qualified URL for the historical date-range XLSX
  // download. Components fetch this directly so they can stream the
  // response as a blob.
  stockHistoryUrl: (fromDate, toDate) =>
    `${portalApi.baseUrl}/api/customer-portal/stock/download-history?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,

  // Stock reconciliation — preview, apply, history
  previewStockReconciliation: async (file, mode) => {
    const url = `${portalApi.baseUrl}/api/customer-portal/stock/reconciliation/preview`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    const token = portalApi.getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (response.status === 401) {
      portalApi.setToken(null);
      window.location.href = '/customer-portal/login';
      throw new Error('Session expired');
    }
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to preview reconciliation');
    return data;
  },
  applyStockReconciliation: (reportId, selectedPalletNumbers) =>
    portalApi.request(`/stock/reconciliation/${reportId}/apply`, {
      method: 'POST',
      body: JSON.stringify({ selected_pallet_numbers: selectedPalletNumbers, confirm: 'APPLY' }),
    }),
  getReconciliationHistory: () => portalApi.request('/stock/reconciliation/history'),
  getReconciliationReport: (reportId) => portalApi.request(`/stock/reconciliation/${reportId}`),
  
  // Delivery Requests
  getDeliveryRequests: () => portalApi.request('/delivery-requests'),
  getDeliveries: () => portalApi.request('/delivery-requests'), // Alias for getDeliveryRequests
  createDeliveryRequest: (data) => portalApi.request('/delivery-requests', { method: 'POST', body: JSON.stringify(data) }),
  submitDeleteRequest: (requestId) => portalApi.request(`/delivery-requests/${requestId}/delete-request`, { method: 'POST' }),
  // Compute the earliest valid delivery date for a list of pallet IDs
  // (server enforces the 2-day rule for incoming stock)
  computeEarliestDeliveryDate: (palletIds) => portalApi.request('/delivery-requests/earliest-date', {
    method: 'POST',
    body: JSON.stringify({ pallet_ids: palletIds }),
  }),

  // Download a comprehensive PDF of a delivery request (with letterhead).
  // Returns a Blob ready for the browser to save.
  downloadDeliveryRequestPdf: async (requestId) => {
    const url = `${portalApi.baseUrl}/api/customer-portal/delivery-requests/${requestId}/details-pdf`;
    const token = portalApi.getToken();
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      portalApi.setToken(null);
      window.location.href = '/customer-portal/login';
      throw new Error('Session expired');
    }
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const errBody = await response.json();
        if (errBody?.detail) detail = errBody.detail;
      } catch (_) { /* non-JSON error body */ }
      throw new Error(detail);
    }
    // Try to extract a friendly filename from the Content-Disposition header
    let filename = `delivery_request_${requestId}.pdf`;
    const cd = response.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    if (match) filename = decodeURIComponent(match[1]);
    return { blob: await response.blob(), filename };
  },
  
  // Order Actions
  submitOrderDeleteRequest: (orderId) => portalApi.request(`/orders/${orderId}/delete-request`, { method: 'POST' }),
  submitOrderChangeRequest: (orderId, data) => portalApi.request(`/orders/${orderId}/change-request`, { method: 'POST', body: JSON.stringify(data) }),
  
  // Notifications
  getNotifications: (unreadOnly = false) => portalApi.request(`/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  markRead: (notificationId) => portalApi.request(`/notifications/${notificationId}/read`, { method: 'PUT' }),
  markAllRead: () => portalApi.request('/notifications/mark-all-read', { method: 'PUT' }),
  
  // Profile
  getProfile: () => portalApi.request('/profile'),
  updateProfile: (data) => portalApi.request('/profile', { method: 'PUT', body: JSON.stringify(data) }),
  
  // 2FA. There is no disable helper: WP-P2F makes two-factor compulsory for
  // the portal, and the server answers /portal/disable-2fa with 403. Only Adela
  // staff can clear it (Clients -> Portal Users -> Reset 2FA).
  setup2FA: () => portalApi.request('/portal/setup-2fa', { method: 'POST' }),
  enable2FA: (code) => portalApi.request('/portal/enable-2fa', { method: 'POST', body: JSON.stringify({ code }) }),
  
  // Analytics
  getProjections: (leadTimeDays = 60, safetyStockPct = 20) =>
    portalApi.request(`/analytics/projections?lead_time_days=${leadTimeDays}&safety_stock_pct=${safetyStockPct}`),
};

// Portal Auth Provider
export const PortalAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('portal_token');
    const userData = localStorage.getItem('portal_user');
    
    if (token && userData) {
      portalApi.setToken(token);
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (username, password, totpCode = null) => {
    const credentials = { username, password };
    if (totpCode) credentials.totp_code = totpCode;
    
    const response = await portalApi.login(credentials);
    
    if (response.requires_2fa) {
      return { requires2FA: true };
    }

    // WP-P2F — two-factor is compulsory. No session is issued until it is set
    // up; the answer carries a 15-minute set-up token instead.
    if (response.requires_2fa_setup) {
      portalApi.setSetupToken(response.setup_token);
      return { requires2FASetup: true, message: response.message };
    }
    
    if (response.success) {
      portalApi.setSetupToken(null);
      portalApi.setToken(response.access_token);
      localStorage.setItem('portal_user', JSON.stringify(response.user));
      setUser(response.user);
      return { success: true };
    }
    
    throw new Error('Login failed');
  };

  // WP-P2F — finishing compulsory set-up finishes the login: /portal/enable-2fa
  // answers with a full session token, so the customer goes straight in.
  const completeTwoFactorSetup = (response) => {
    if (!response?.access_token) return false;
    portalApi.setSetupToken(null);
    portalApi.setToken(response.access_token);
    if (response.user) {
      localStorage.setItem('portal_user', JSON.stringify(response.user));
      setUser(response.user);
    }
    return true;
  };

  const logout = () => {
    portalApi.setToken(null);
    portalApi.setSetupToken(null);
    localStorage.removeItem('portal_user');
    setUser(null);
    window.location.href = '/customer-portal/login';
  };

  return (
    <PortalAuthContext.Provider value={{ user, loading, login, logout, completeTwoFactorSetup, portalApi }}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export { portalApi };
export default PortalAuthProvider;
